import { useCallback, useRef, useState } from "react";
import type { SetURLSearchParams } from "react-router";
import { useAppToast, type AddAppToastInput } from "@/components/AppToast";
import type {
  CalculationFilters,
  DeleteCalculationCandidate,
} from "../components/CalculationInputList/CalculationInputList.types";
import { listStateToSearchParams } from "../components/CalculationInputList/listStateSearchParams";
import useDeleteCalculation from "./useDeleteCalculation";

interface UseDeleteCalculationConfirmationOptions {
  applied: CalculationFilters;
  currentPage: number;
  pageSize: number;
  totalElements: number;
  setSearchParams: SetURLSearchParams;
  onRefresh: () => void;
  deleteFailureToast: AddAppToastInput;
}

const useDeleteCalculationConfirmation = ({
  applied,
  currentPage,
  pageSize,
  totalElements,
  setSearchParams,
  onRefresh,
  deleteFailureToast,
}: UseDeleteCalculationConfirmationOptions) => {
  const { addToast } = useAppToast();
  const targetRef = useRef<HTMLElement>(null!);
  const [candidate, setCandidate] = useState<DeleteCalculationCandidate | null>(
    null,
  );

  const handleDeleteSuccess = useCallback(() => {
    setCandidate(null);

    const nextTotalElements = Math.max(totalElements - 1, 0);
    const nextTotalPages = Math.ceil(nextTotalElements / pageSize);
    const targetPage = Math.min(currentPage, Math.max(nextTotalPages - 1, 0));

    setSearchParams(listStateToSearchParams(applied, targetPage, pageSize));
    onRefresh();
  }, [
    applied,
    currentPage,
    onRefresh,
    pageSize,
    setSearchParams,
    totalElements,
  ]);

  const handleDeleteFailure = useCallback(() => {
    setCandidate(null);
    addToast(deleteFailureToast);
  }, [addToast, deleteFailureToast]);

  const { deleteCalculation, loading } = useDeleteCalculation({
    onSuccess: handleDeleteSuccess,
    onError: handleDeleteFailure,
  });

  const requestDelete = useCallback(
    (nextCandidate: DeleteCalculationCandidate) => {
      setCandidate(nextCandidate);
    },
    [],
  );

  const cancelDelete = useCallback(() => {
    setCandidate(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!candidate) return;
    deleteCalculation(candidate.id);
  }, [candidate, deleteCalculation]);

  return {
    active: candidate !== null,
    candidate,
    loading,
    targetRef,
    requestDelete,
    cancelDelete,
    confirmDelete,
  };
};

export default useDeleteCalculationConfirmation;
